import React from "react";
import { useTranslation } from "react-i18next";
import { Identifiable, Resource } from "@planzone/dto";
import {
  useAuthentication,
  useRetrieveConstraints,
  useRetrievePersonalSynthesis,
} from "@planzone/view-models";
import { events } from "@planzone/events";
import { useMessage } from "@components/useMessage";
import { usePlanzonePermissions } from "@components/permissions";
import { Props } from "./types";
import { Tasks } from "./tasks";
import { isBlocked } from "./tasks/task/utils";
import { Section } from "./section";
import { BlockedTasksToggle } from "./blockedTasksToggle";
import { TasksLoader } from "./tasksLoader";
import { ResourceSelection } from "@common/resourceSelection";
import { CalendarWidget } from "./calendarWidget";
import styles from "./styles.scss";
import { usePreferences } from "@components/preferences";

function useResize(
  direction: "horizontal" | "vertical",
  containerRef: React.RefObject<HTMLDivElement | null>,
) {
  const [percent, setPercent] = React.useState<number | null>(null);
  const canMove = React.useRef(false);

  React.useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!canMove.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const next =
        direction === "horizontal"
          ? Math.round(((e.clientX - rect.left) / rect.width) * 100)
          : Math.round(((e.clientY - rect.top) / rect.height) * 100);
      if (next >= 0 && next <= 100) setPercent(next);
    };
    const onMouseUp = () => {
      if (canMove.current) {
        canMove.current = false;
        document.documentElement.classList.remove(styles.unselectable);
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  });

  return {
    percent,
    startResize: () => {
      canMove.current = true;
      document.documentElement.classList.add(styles.unselectable);
    },
  };
}

function Synthesis(_: Props) {
  const { t } = useTranslation();
  const { account } = useAuthentication();
  const { isRequestPending, synthesis, retrievePersonalSynthesis } =
    useRetrievePersonalSynthesis();
  const { constraints } = useRetrieveConstraints();
  const { isAdministrator } = usePlanzonePermissions();
  const [selectedResource, setSelectedResource] = React.useState<
    Identifiable<Resource>
  >(account.resource);

  const { state, edit } = usePreferences();
  const toggleShowBlockedTasks = () =>
    edit({ ...state, showBlockedTasks: !state.showBlockedTasks });

  const upComingTasks = state.showBlockedTasks
    ? synthesis.upComingTasks
    : synthesis.upComingTasks.filter((task) => !isBlocked(task, constraints));

  const overDueTasks = state.showBlockedTasks
    ? synthesis.overDueTasks
    : synthesis.overDueTasks.filter((task) => !isBlocked(task, constraints));

  React.useEffect(() => {
    retrievePersonalSynthesis(selectedResource);
  }, [selectedResource]);

  useMessage(
    ({ type, data }) =>
      type === events.markTaskAsCompletedSucceeded ||
      (type === events.editTaskSucceeded &&
        data.status.percentComplete === 100) ||
      type === events.editAllocationSucceeded ||
      type === events.createTaskSucceeded ||
      (type === events.allocateResourcesSucceeded &&
        data.allocations.find(
          ({ resource }) =>
            resource.uuid === account.resource.uuid ||
            resource.uuid === selectedResource.uuid,
        ) &&
        retrievePersonalSynthesis(selectedResource)),
  );

  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const tasksColumnRef = React.useRef<HTMLDivElement | null>(null);

  const horizontalResize = useResize("horizontal", wrapperRef);
  const verticalResize = useResize("vertical", tasksColumnRef);

  return (
    <div className={styles.planzone}>
      <div className={styles.header}>
        <p className={styles.doneTasks}>
          {t("personalSynthesis.doneTasks")}:{" "}
          <span className={styles.doneTasksTag}>
            {synthesis.completedTasksNbr}
          </span>
        </p>
        <div className={styles.filters}>
          <BlockedTasksToggle
            isToggled={state.showBlockedTasks}
            toggle={toggleShowBlockedTasks}
          />
          {isAdministrator && (
            <div className={styles.resourceSelector}>
              <p className={styles.resourceSelectorLabel}>
                {t("personalSynthesis.synthesisOf")}
              </p>
              <ResourceSelection
                placeholder={""}
                selected={selectedResource}
                onSelect={setSelectedResource}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.main}>
        <div className={styles.wrapper} ref={wrapperRef}>
          <div
            className={styles.tasksColumn}
            ref={tasksColumnRef}
            style={
              horizontalResize.percent !== null
                ? { width: `${horizontalResize.percent}%`, flex: "none" }
                : undefined
            }
          >
            <div
              className={styles.tasks}
              style={
                verticalResize.percent !== null
                  ? { height: `${verticalResize.percent}%`, flex: "none" }
                  : undefined
              }
            >
              <Section
                title={
                  <p className={styles.row}>
                    {t("personalSynthesis.upComingTasks")}{" "}
                    <span className={styles.upComingTasksTag}>
                      {upComingTasks.length}
                    </span>
                  </p>
                }
              >
                {!isRequestPending ? (
                  <Tasks tasks={upComingTasks} constraints={constraints} />
                ) : (
                  <TasksLoader />
                )}
              </Section>
              <div
                className={styles.verticalResizeHandle}
                onMouseDown={verticalResize.startResize}
              />
            </div>
            <div className={styles.tasks}>
              <Section
                title={
                  <p className={styles.row}>
                    {t("personalSynthesis.overDueTasks")}{" "}
                    <span className={styles.delayedTasksTag}>
                      {overDueTasks.length}
                    </span>
                  </p>
                }
              >
                {!isRequestPending ? (
                  <Tasks
                    tasks={overDueTasks}
                    areLate
                    constraints={constraints}
                  />
                ) : (
                  <TasksLoader />
                )}
              </Section>
            </div>
            <div
              className={styles.horizontalResizeHandle}
              onMouseDown={horizontalResize.startResize}
            />
          </div>
          <div className={styles.calendarColumn}>
            <CalendarWidget resource={selectedResource} />
          </div>
        </div>
      </div>
    </div>
  );
}

export { Synthesis };
